/**
 * Everything the local user does to their own devices.
 *
 * Capture and publication are kept in lockstep here: turning a device off both
 * unpublishes it AND stops the capture, so the OS camera light and the mic
 * indicator tell the truth. A muted-but-live track would not.
 *
 * DEVICE HOT-SWAP: switching a camera or microphone mid-call re-captures and
 * `replaceTrack`s the existing publication — no renegotiation, no black frame —
 * and the effect / denoise chains are rebuilt on the new source automatically.
 * Switching a SPEAKER goes to the audio sink, which applies `setSinkId`.
 */
import { QUALITY_PROFILES, type QualityProfile } from '@cyphex/types';
import type {
  DeviceKind,
  ScreenShareHint,
  ShareStartOptions,
  ShareStartResult,
  ShareStopCause,
} from './contract';
import type { AudioSink } from './livekitAudio';
import type { LocalPublisher } from './livekitPublisher';
import { createLocalMedia, type LocalMediaController } from './localMedia';
import { blockingPresenter, nextShareClaim, type ShareClaimant } from './shareArbiter';
import type { AudioProcessingPreferences, VideoEffectPreferences } from './settings/preferences';

export interface LocalControlsDeps {
  getPublisher(): LocalPublisher | null;
  getSink(): AudioSink | null;
  getProfile(): QualityProfile;
  isConnected(): boolean;
  /** Re-read the roster after a publication change. */
  onPublicationsChanged(): void;
  /** A processing chain changed state (denoiser live, effect degraded). */
  onProcessingChanged(): void;
  /** Applied instead, while there is no room to read the truth from. */
  patchLocal(patch: { cameraOn?: boolean; micOn?: boolean }): void;
  onShareOwnerChanged(owner: 'local' | null): void;
  /**
   * A DISPLAY CAPTURE OPENED OR CLOSED - say so NOW, not on the next room event.
   *
   * `shareCapturing` reaches the UI through the provider's snapshot, and the
   * provider only re-read it on a roster sync. A start's capture resolves a
   * whole signal round trip before its publish lands and nothing in that window
   * syncs the roster, so for that window the OS share indicator was lit and the
   * app's own Share button still said "Share": pressing it stopped nothing, it
   * opened the chooser for a SECOND capture. The browser's floating bar was the
   * only Stop in existence.
   */
  onShareCaptureChanged(): void;
  /**
   * The live roster, for the one-presenter guard. Empty before the room exists.
   *
   * `shareDisabled` rides along because a host's ban is replicated on the same
   * roster as everything else, and the Share button must obey it BEFORE it
   * captures anything — see `startScreenShare`.
   */
  getClaimants(): readonly (ShareClaimant & {
    readonly name?: string;
    readonly shareDisabled?: boolean;
  })[];
  /** This session's SFU identity. */
  getLocalId(): string;
  /**
   * Writes this client's stage claim as a replicated participant attribute, or
   * clears it with `null`. Awaited BEFORE the track is published so the claim
   * reaches every other client ahead of the share it explains.
   */
  setShareClaim(claim: number | null): Promise<void>;
}

export interface LocalControls {
  readonly media: LocalMediaController;
  setWanted(camera: boolean, mic: boolean): void;
  /** Makes the wire match `wanted` under the current profile. Idempotent. */
  syncPublications(): Promise<void>;
  setMicEnabled(on: boolean): Promise<void>;
  setCameraEnabled(on: boolean): Promise<void>;
  setDevice(kind: DeviceKind, deviceId: string): Promise<void>;
  setVideoEffect(preferences: VideoEffectPreferences): Promise<void>;
  setAudioProcessing(preferences: AudioProcessingPreferences): Promise<void>;
  startScreenShare(
    hint: ScreenShareHint,
    takeOver?: boolean,
    options?: ShareStartOptions,
  ): Promise<ShareStartResult>;
  /**
   * Resolves `true` when the capture is genuinely released.
   *
   * `cause` is for the SENTENCE, never for the stop: a start whose publish is
   * still in the air reads it to say which stop ended it rather than blaming
   * the person for a host's. Default `self` - the toolbar, or the browser's bar.
   */
  stopScreenShare(cause?: ShareStopCause): Promise<boolean>;
  /**
   * A start is between its claim and its publish RIGHT NOW.
   *
   * The one thing only this client can know, and the reason it has to be
   * asked: during that window this client legitimately holds a claim with no
   * track behind it, which is indistinguishable from the phantom claim the
   * provider clears (`shareArbiter.holdsPhantomClaim`).
   */
  readonly shareStarting: boolean;
  /** The OS truth: a display capture is open right now. */
  readonly shareCapturing: boolean;
  /**
   * A PUBLICATION OF THIS CLIENT'S THAT NOTHING IS FEEDING.
   *
   * The SFU is carrying a screen share of ours, no display capture is open —
   * `captureLive`, so a track the browser's Stop bar ended counts as no capture
   * even though the stream object is still there — and no start or stop is in
   * flight to explain either. A publication in that state
   * can only be carrying a `MediaStreamTrack` that has already been stopped — so
   * the stage shows a frozen frame, `PresenterBar` tells this client it is
   * presenting to everyone, and every OTHER participant's Share is refused BY
   * THIS CLIENT'S NAME.
   *
   * Nothing in `shareArbiter` can repair it: `resolveShareOwner` gives this
   * client the stage, so `mustYieldShare` is false, and `sharing` is true, so
   * `holdsPhantomClaim` is false. Only the client that owns the capture knows,
   * which is why it is asked here and acted on in the provider — see
   * `livekitProvider.clearOrphanedShare`.
   */
  readonly shareOrphaned: boolean;
  /** Re-applies a speaker chosen before connect, once the sink exists. */
  restoreOutputDevice(): Promise<void>;
}

export function createLocalControls(deps: LocalControlsDeps): LocalControls {
  const media = createLocalMedia(deps.onProcessingChanged);
  let cameraWanted = false;
  let micWanted = false;
  let cameraDeviceId: string | undefined;
  let micDeviceId: string | undefined;
  let outputDeviceId = '';
  /** Between this client's claim and its publish. See `LocalControls.shareStarting`. */
  let starting = false;
  /**
   * The claim this client is presenting UNDER, and the hint it captured with,
   * kept so a rejoin can put the same share back on the wire — see `resumeShare`.
   */
  let heldClaim: number | null = null;
  let heldHint: ScreenShareHint | null = null;
  /**
   * STOPS, COUNTED — the whole of "a share that was stopped while it was being
   * published must not end up published".
   *
   * A publish is an await: a signal round trip plus a renegotiation for a new
   * video transceiver, 100 ms to a second on a bad link. It cannot be recalled
   * once it is in the air, and the control most likely to end a share inside
   * that window is the one THE BROWSER ITSELF puts on screen — its floating
   * "Stop sharing" bar, whose `ended` event runs `stopShare` to completion:
   * unpublish (nothing there yet), capture released, claim withdrawn. The track
   * then landed behind it and every one of those undos was silently reversed.
   *
   * A re-check alone cannot fix that, because by the time the check can run the
   * track is already up. So the publish is UNDONE rather than prevented: the
   * start reads this counter before it publishes and again after, and a count
   * that has moved means the share it belongs to no longer exists — take the
   * track back down, release whatever is captured, clear the claim, and tell the
   * caller `stopped` so nothing reports a share that is not there.
   *
   * COUNTING IS NOT THE WHOLE ANSWER, because a stop can happen that nothing
   * counts: an `ended` event dispatched at a track no listener had been attached
   * to yet. `shareEnded` asks the track as well as this counter.
   */
  let stops = 0;
  /**
   * WHOSE STOP THE LAST ONE WAS - a host's, a lost race's, or this person's.
   *
   * `stops` says a share ended; it cannot say who ended it, and a start caught
   * inside its own publish window has nothing else to read. Without this the
   * one sentence it can produce ("You stopped it while it was still starting")
   * was printed for a host's Stop too, underneath the true sentence the
   * provider had already raised for the same event.
   */
  let lastStopCause: ShareStopCause = 'self';
  /**
   * A share verb — a start or a stop — is running right now. Depth-counted,
   * because a start that fails stops itself on the way out.
   *
   * Deliberately WIDER than `starting`, which is the claim→publish window and
   * must stay that way for `shareArbiter.holdsPhantomClaim`. This one exists for
   * `shareOrphaned`: a publication with no capture behind it is rubbish UNLESS a
   * verb is in the middle of putting one there or taking it away.
   */
  let busy = 0;
  /**
   * This client believes a screen-share publication of its own exists at the
   * SFU. Set when a publish lands, cleared only when an unpublish is ACCEPTED —
   * a refused unpublish (a reconnecting room) leaves one behind that nothing is
   * feeding, and `shareOrphaned` is what gets it taken down.
   */
  let published = false;

  /**
   * A DISPLAY CAPTURE IS OPEN — a video track that has not ENDED.
   *
   * `media.screenStream !== null` is a DIFFERENT question, and the difference is
   * a whole half of this defect. The browser's own floating "Stop sharing" bar
   * ends the TRACK; nothing in this app is holding a reference it has to null,
   * so the stream object stays exactly where it was and every reading built on
   * it goes on saying a capture is running. A stream whose video track has ended
   * is a capture that is over, and reading it as one is what lets the orphan
   * sweep see a publication that nothing is feeding.
   *
   * Fails towards "still capturing": a track that cannot say — an engine with no
   * `readyState`, or a stream with no video track at all — is treated as live,
   * because being wrong that way costs a button that offers Stop, and being
   * wrong the other way costs an OS indicator nothing can turn off.
   */
  const streamLive = (stream: MediaStream): boolean => {
    const video = stream.getVideoTracks();
    return video.length === 0 || video.some((track) => track.readyState !== 'ended');
  };

  /** The capture this client is holding RIGHT NOW, if it is still open. */
  const captureLive = (): boolean => {
    const stream = media.screenStream;
    return stream !== null && streamLive(stream);
  };

  /**
   * THE SHARE THIS PUBLISH BELONGS TO NO LONGER EXISTS.
   *
   * One question, asked after every publish, because there are TWO ways for a
   * share to stop existing inside its own publish window and only the first was
   * being asked about:
   *
   *   1. A stop was COUNTED — the toolbar's Stop, a host's, the provider's
   *      yield, or the `ended` listener `startScreenShare` registers.
   *   2. THE CAPTURE ENDED WITH NOBODY LISTENING. That listener used to be
   *      registered AFTER the claim write, so a browser Stop bar pressed inside
   *      that round trip fired `ended` into a track with no listener on it: the
   *      event was lost, no stop was counted, and the publish landed behind it
   *      on a dead capture — the presenter told "You are presenting to
   *      everyone" over a frozen frame, every other Share refused by their name,
   *      and NOTHING self-healing it, because the stream reference was still
   *      there and `shareOrphaned` reads a capture, not a track. It is now
   *      registered before the first await; the track is asked as well, so the
   *      answer never depends on an event arriving.
   *
   * It is asked of THE STREAM THIS PUBLISH BELONGS TO, never of whatever the app
   * happens to be holding by then. A capture superseded by a second press has
   * already had its tracks stopped by `localMedia`, so it answers for itself —
   * and asking `media.screenStream` instead would let one start's publish undo
   * the NEXT one's share, which is a worse bug than the one being closed.
   */
  const shareEnded = (stopsBefore: number, stream: MediaStream): boolean =>
    stopsBefore !== stops || !streamLive(stream);

  /**
   * DOES THIS START STILL OWN THE SHARE IT IS ABOUT TO UNDO?
   *
   * THE INVARIANT: AN UNDO MAY ONLY EVER TOUCH THE SHARE IT OWNS.
   *
   * `shareEnded` is DETECTION and it is per-stream, which is right. The ACTION
   * it triggers is not: `stopShare` unpublishes whatever publication exists,
   * stops whatever capture is current and clears the claim, whoever put them
   * there. So a start whose own share had been superseded by a SECOND press
   * tore down the share that replaced it - press Share, press Share again
   * inside the first publish window, and the room ends with no share at all and
   * a sentence blaming the person for stopping one they had just asked for.
   * That is the INVERSE of the failure the per-stream detection closed, and it
   * is reachable from the shipping toolbar in two presses.
   *
   * The two questions fail in OPPOSITE directions, deliberately:
   *   * DETECTION fails towards "still capturing" - being wrong that way costs
   *     a button that offers Stop; being wrong the other way costs an OS
   *     indicator nothing in the app can turn off.
   *   * THE ACTION fails towards "do nothing" - a stale publication is
   *     collected by the provider's orphan sweep a moment later, while a share
   *     torn down by somebody else's tidying up is not recoverable by anything.
   *
   * Ownership, over the three states the capture slot can be in:
   *   * `media.screenStream === stream` - mine, ended or not. Undo.
   *   * a DIFFERENT stream - a later start owns the capture, and with it the
   *     publication, the claim and the undo. Touch nothing.
   *   * `null` - the slot is empty, and which of us emptied it decides. A stop
   *     this start HEARD (`stops` moved) released it and left this publish to
   *     finish undoing, so undo. Otherwise a later start released it on its way
   *     to its own chooser and is still holding the answer; keep off it.
   */
  const ownsShare = (stopsBefore: number, stream: MediaStream): boolean => {
    const current = media.screenStream;
    if (current === stream) return true;
    if (current !== null) return false;
    return stops !== stopsBefore;
  };

  /**
   * WHICH stop ended a start that was still publishing.
   *
   * A count that never moved means nothing counted one - the capture ended
   * underneath us, which is the browser's own floating bar and therefore the
   * person themselves.
   */
  const stopCause = (stopsBefore: number): ShareStopCause =>
    stops === stopsBefore ? 'self' : lastStopCause;

  /**
   * PUT A LIVE SHARE BACK ON THE WIRE — after a rejoin, and nowhere else.
   *
   * `openRoom` preserves capture across a rejoin on purpose (no second
   * permission prompt, no black frame) and then synced mic and camera only. A
   * presenter dropped by Chrome's `freeze` on a backgrounded tab therefore came
   * back with their screen still being captured, the OS share indicator still
   * lit, and NOTHING on the wire — a demo that had silently ended, to a room
   * that had silently lost it.
   *
   * THE CLAIM IS NOT RE-MINTED. Republishing under the claim this client
   * already holds is the whole safety of this: if somebody took the stage
   * during the drop, their claim is newer, the arbiter gives it to them and
   * this client's own provider yields the moment the roster syncs. A fresh
   * `nextShareClaim` would do the opposite — come back from a network blip and
   * silently steal the stage from whoever picked it up. A rejoin is not a
   * take-over.
   */
  const resumeShare = async (): Promise<void> => {
    const publisher = deps.getPublisher();
    if (!publisher || publisher.screenPublished) return;
    if (starting || heldClaim === null || heldHint === null) return;
    const stream = media.screenStream;
    if (stream === null) return;
    /* A host banned this participant while the room was down. Republishing
       would be refused at 403 and would put the claim back on the wire; the
       provider is about to stop this share for real. */
    const localId = deps.getLocalId();
    if (deps.getClaimants().some((c) => c.id === localId && c.shareDisabled === true)) return;

    const claim = heldClaim;
    const hint = heldHint;
    /* The same window `startScreenShare` has, and the same rule: a rejoin's
       republish is an await, and a Stop pressed inside it must not be undone by
       the track landing behind it. */
    const stopsBefore = stops;
    starting = true;
    busy += 1;
    try {
      await deps.setShareClaim(claim).catch(() => undefined);
      /* SUPERSEDED INSIDE THE CLAIM WRITE. `publishScreen` reads whatever
         capture is CURRENT, so republishing here would put this rejoin's OLD
         claim under a NEW start's share - and the undo below would then take
         that share down. An undo may only ever touch the share it owns. */
      if (!ownsShare(stopsBefore, stream)) return;
      await publisher.publishScreen(hint);
      published = publisher.screenPublished;
    } catch {
      /* The room is still not ready. The capture is deliberately left alone —
         the next sync tries again, and the Share button can always end it. */
    } finally {
      starting = false;
      busy -= 1;
    }
    /* SCOPED, exactly as in `startScreenShare`: the detection is per-stream and
       so is the action. See `ownsShare`. */
    if (shareEnded(stopsBefore, stream) && ownsShare(stopsBefore, stream)) await stopShare();
  };

  const syncPublications = async (): Promise<void> => {
    const publisher = deps.getPublisher();
    if (!publisher) return;
    if (micWanted) await publisher.syncMic();
    else await publisher.unpublishMic();

    const videoAllowed = QUALITY_PROFILES[deps.getProfile()].videoEnabled;
    if (cameraWanted && videoAllowed) await publisher.syncCamera();
    else await publisher.unpublishCamera();
    await resumeShare();
    deps.onPublicationsChanged();
  };

  /**
   * STOP PRESENTING — and release the capture, whatever else fails.
   *
   * `unpublishScreen` reaches the SFU, and reaching the SFU is exactly what a
   * reconnecting or disconnected room cannot do: `unpublishTrack` rejects. That
   * rejection used to take `media.stopScreenShare()` with it, so the one line
   * that turns the OS share indicator off was skipped precisely when the
   * network was bad — and the caller was told the share had been stopped. A
   * person who believes they stopped sharing while their screen is still being
   * captured is the worst outcome this feature has.
   *
   * So the order is inverted in effect: the unpublish is attempted, its failure
   * is remembered rather than thrown, and the CAPTURE is released
   * unconditionally. The answer says whether the wire really was cleared, and
   * only a clean stop is allowed to become a notice.
   *
   * Returning `false` is not "nothing happened": the capture is gone either way.
   * It means the SFU may still be forwarding a track this client can no longer
   * feed, which the next roster sync resolves.
   */
  const stopShare = async (cause: ShareStopCause = 'self'): Promise<boolean> => {
    /* COUNTED BEFORE ANYTHING IS AWAITED, and that line is the fix: a publish
       already in the air cannot be recalled, so what makes "stopped" mean
       stopped inside that window is that the start reads this again when its
       publish lands, sees it has moved, and takes the track back down. */
    stops += 1;
    /* WHOSE stop this is, recorded for the start that may be publishing right
       now: it reads `stops` again when its publish lands, and a count that has
       moved tells it the share is over but not who ended it. */
    lastStopCause = cause;
    busy += 1;
    let clean = true;
    try {
      await deps.getPublisher()?.unpublishScreen();
      /* Only an ACCEPTED unpublish means this client's publication is gone. A
         refused one leaves a share at the SFU that nothing is feeding, which is
         what `shareOrphaned` exists to have taken down on the next sync. */
      published = false;
    } catch {
      clean = false;
    }
    heldClaim = null;
    heldHint = null;
    media.stopScreenShare();
    /* The OS indicator is out NOW; the claim clear below is a round trip. The
       button must not go on offering Stop for a capture that is already gone. */
    deps.onShareCaptureChanged();
    /* The claim goes with the share. Left behind, it would keep winning
       `nextShareClaim`'s max for the rest of the session and — since
       `shareArbiter.pendingPresenter` blocks a start on a claim whose track has
       not landed yet — it would block the whole room from presenting. The
       provider re-checks for exactly that on every roster sync, because this
       clear is best-effort and the room it has to reach may be the one that
       just refused the unpublish above. */
    const cleared = await deps
      .setShareClaim(null)
      .then(() => true)
      .catch(() => false);
    busy -= 1;
    deps.onPublicationsChanged();
    if (!deps.isConnected()) deps.onShareOwnerChanged(null);
    return clean && cleared;
  };

  const setOutput = async (deviceId: string): Promise<void> => {
    outputDeviceId = deviceId;
    await deps.getSink()?.setOutput(deviceId).catch(() => undefined);
  };

  return {
    media,
    setWanted(camera, mic) {
      cameraWanted = camera;
      micWanted = mic;
    },
    syncPublications,

    async setMicEnabled(on) {
      micWanted = on;
      if (on) await media.setMic(true, micDeviceId).catch(() => null);
      await syncPublications();
      if (!on) await media.setMic(false).catch(() => null);
      if (!deps.isConnected()) deps.patchLocal({ micOn: on });
    },

    async setCameraEnabled(on) {
      cameraWanted = on;
      if (on) {
        // A permission denial must reach the caller's toast, but must not leave
        // the client believing it has a camera to publish.
        await media.setCamera(true, cameraDeviceId).catch((error: unknown) => {
          cameraWanted = false;
          throw error;
        });
      }
      await syncPublications();
      if (!on) await media.setCamera(false).catch(() => null);
      if (!deps.isConnected()) deps.patchLocal({ cameraOn: on });
    },

    async setDevice(kind: DeviceKind, deviceId) {
      if (kind === 'audiooutput') {
        await setOutput(deviceId);
        return;
      }
      if (kind === 'videoinput') {
        cameraDeviceId = deviceId;
        cameraWanted = true;
        await media.setCamera(true, deviceId).catch(() => null);
      } else {
        micDeviceId = deviceId;
        micWanted = true;
        await media.setMic(true, deviceId).catch(() => null);
      }
      await syncPublications();
    },

    async setVideoEffect(preferences) {
      await media.setVideoEffect(preferences);
      // Hot-swaps the published track — no renegotiation, no black frame.
      await deps.getPublisher()?.syncCamera().catch(() => undefined);
      deps.onPublicationsChanged();
    },

    async setAudioProcessing(preferences) {
      await media.setAudioProcessing(preferences);
      await deps.getPublisher()?.syncMic().catch(() => undefined);
      deps.onPublicationsChanged();
    },

    /**
     * ONE PRESENTER AT A TIME, in the order that makes the refusals honest.
     *
     *   0. Obey the HOST. A participant whose share a host revoked is refused
     *      here, before anything is captured — the SFU would refuse the publish
     *      at 403 anyway, and discovering a ban by having a capture start, a
     *      claim land and a publish fail is how a banned user ends up holding a
     *      live capture nothing will stop.
     *   1. Ask BEFORE capturing. A refusal must never open the browser's
     *      chooser — or, on the desktop client, the native source picker — only
     *      to throw the user's answer away.
     *   2. Capture.
     *   3. ASK AGAIN. The chooser can sit open for a minute, and somebody can
     *      start presenting inside it. Without this the Lamport claim would be
     *      NEWER than theirs and would silently take the stage off them — the
     *      exact accident the first guard exists to prevent. The capture is
     *      released, so the OS share indicator goes away with it.
     *   4. Claim, then publish, in that order.
     *
     * BOTH guards ask `blockingPresenter`, not `currentPresenter`, and that is
     * the difference between the paragraph above being true and being a wish.
     * Step 4 puts the claim on the wire a round trip before the track, so a
     * presenter who is starting is `sharing: false` with a real claim for as
     * long as a renegotiation takes. A guard that only saw published tracks
     * looked straight through that window at an empty stage and let the SECOND
     * presser through — whose claim, minted afterwards, is higher, and who
     * therefore took the stage off the person who pressed Share first.
     *
     * What is left after all of it is the honest race — two people pressing
     * Share in the same instant, each looking at an empty stage and neither
     * holding a claim yet. Both publish; every client, both presenters
     * included, agrees on the winner from the claims a moment later, and the
     * loser's own provider stops it.
     */
    async startScreenShare(hint, takeOver = false, options = {}) {
      const localId = deps.getLocalId();
      const banned = (): boolean =>
        deps.getClaimants().some((c) => c.id === localId && c.shareDisabled === true);
      if (banned()) return { status: 'denied' };
      if (!takeOver) {
        const blocking = blockingPresenter(deps.getClaimants(), localId);
        if (blocking !== null) return { status: 'blocked', presenter: blocking };
      }

      /*
       * THE CHOOSER IS PART OF THE START, and `busy` is how the provider's
       * orphan sweep knows a share verb is running. It used to open only at the
       * claim write, which left the chooser - where a start spends nearly all
       * of its life - invisible: `localMedia` releases the previous capture on
       * the way in, so the sweep could see a publication with nothing feeding
       * it, run `stopScreenShare`, and bump the capture generation out from
       * under a request it does not own. The same invariant one level up: an
       * undo cannot own a capture that has not been handed over yet.
       */
      busy += 1;
      let stream: MediaStream | null = null;
      try {
        stream = await media.startScreenShare(hint, deps.getProfile(), options);
      } finally {
        busy -= 1;
        /*
         * THE OS TRUTH REACHES THE APP NOW, NOT ON THE NEXT ROOM EVENT.
         *
         * `shareCapturing` gets into the snapshot through the provider, which
         * only re-read it on a roster sync - and nothing syncs the roster
         * between this line and the publish landing, a whole signal round trip
         * later. For that window the OS share indicator was lit and the app's
         * own Share button still said "Share"; pressing it stopped nothing, it
         * opened the chooser for a SECOND capture, and the browser's floating
         * bar was the only Stop that existed. In `finally` because a chooser
         * the user DISMISSED changed the answer too: the previous capture was
         * released before it opened.
         */
        deps.onShareCaptureChanged();
      }
      /* A capture that handed back nothing is not a share, and reporting
         `started` for it is the whole defect this file exists to prevent: the
         presenting bar lights, every other participant's Share is refused by
         this client's name, and a claim sits on the wire for a stage nobody is
         on. */
      if (!stream) return { status: 'stopped', stoppedBy: 'self' };

      /* The chooser was open for as long as the user took. A ban can have
         landed inside it just as easily as another presenter can. */
      if (banned()) {
        media.stopScreenShare();
        deps.onShareCaptureChanged();
        return { status: 'denied' };
      }
      if (!takeOver) {
        const blocking = blockingPresenter(deps.getClaimants(), localId);
        if (blocking !== null) {
          media.stopScreenShare();
          deps.onShareCaptureChanged();
          return { status: 'blocked', presenter: blocking };
        }
      }

      /*
       * THE BROWSER'S OWN STOP BAR — LISTENED TO BEFORE THE FIRST AWAIT.
       *
       * This registration used to sit after the claim write, one `await` too
       * late. A DOM `ended` event is a TASK: it cannot interrupt the
       * synchronous stretch between the capture resolving and this line, but it
       * can and does land inside the round trip that follows — and an event
       * dispatched at a track with no listener on it is simply gone. No stop was
       * counted, so the publish that followed looked untroubled, and the room
       * got a share whose capture had already ended.
       *
       * `track.stop()` does not fire `ended` (it is specified not to), so this
       * listener cannot be tripped by the app releasing its own capture — only
       * by the capture ending underneath it, which is exactly the event worth
       * hearing.
       */
      stream.getVideoTracks()[0]?.addEventListener('ended', () => void stopShare());

      const claim = nextShareClaim(Date.now(), deps.getClaimants());
      /* THE STOP COUNT THIS PUBLISH BELONGS TO. Read before the claim write,
         because a Stop can land inside that await as easily as inside the
         publish, and both are this share being stopped while it starts. */
      const stopsBefore = stops;
      starting = true;
      busy += 1;
      const publisher = deps.getPublisher();
      try {
        await deps.setShareClaim(claim).catch(() => undefined);
        /*
         * SUPERSEDED INSIDE THE CLAIM WRITE - so nothing below is ours.
         *
         * A second Share press releases this capture and takes the slot.
         * `heldClaim`/`heldHint` would then describe THIS start underneath THAT
         * start's share, so a later rejoin would republish the wrong claim;
         * `publishScreen` reads whatever capture is current, so publishing here
         * puts the other one's track up a second time and overwrites the
         * publication handle that unpublishes it; and the undo below would take
         * the whole thing down. The later start owns the share, the
         * publication, the claim AND the undo. This one answers `stopped`,
         * because no share of ITS is running, having touched none of them.
         */
        if (!ownsShare(stopsBefore, stream)) {
          return { status: 'stopped', stoppedBy: stopCause(stopsBefore) };
        }
        heldClaim = claim;
        heldHint = hint;
        await publisher?.publishScreen(hint);
        published = publisher?.screenPublished ?? false;
      } catch (error: unknown) {
        /*
         * THE PUBLISH WAS REFUSED — so there is no share, and there must be no
         * capture and no claim either.
         *
         * livekit-client throws `PublishTrackError(403)` when the SFU has
         * narrowed `canPublishSources`, which is precisely what a host's Stop
         * does. Left as it was, the presenter got "Could not start screen
         * sharing", a live capture, a lit OS indicator, a claim on the wire
         * blocking the rest of the room — and a Share button that would have
         * started a SECOND capture rather than ending the first.
         */
        starting = false;
        /* Scoped like every other undo here: a refused publish unwinds ITS OWN
           share, and a start superseded while that refusal was in the air has
           no share of its own left to unwind. */
        if (ownsShare(stopsBefore, stream)) await stopShare();
        throw error;
      } finally {
        starting = false;
        busy -= 1;
      }
      /*
       * THE SHARE WAS STOPPED WHILE THIS PUBLISH WAS IN THE AIR.
       *
       * Every other await in this function got a re-check — `banned()` and
       * `blockingPresenter()` both run again after the chooser. The publish
       * needs more than a re-check, because by the time one can run the track is
       * already up: it has to be UNDONE. `stopShare` is the whole undo and it is
       * idempotent, so it is simply run again — the track is unpublished, the
       * capture (already released) stays released, and the claim is re-cleared,
       * this time strictly AFTER the write that raced it.
       *
       * Without this the presenter was told "You are presenting to everyone"
       * over a frozen frame, every other participant's Share was refused by
       * their name, and nothing healed it: the yield does not fire on a client
       * that owns the stage, the phantom-claim sweep does not fire on one that
       * is `sharing`, and the host sweep does not fire without a ban.
       */
      if (shareEnded(stopsBefore, stream)) {
        /* Read BEFORE the undo, which counts a stop of its own. */
        const stoppedBy = stopCause(stopsBefore);
        /*
         * AND THE UNDO IS SCOPED - the inverse failure, and the one the
         * per-stream detection did NOT close. `shareEnded` asks about THIS
         * stream, which is right; `stopShare` asks about nothing at all. A
         * start superseded by a second press therefore took down the share that
         * had replaced it and told the person they had stopped it.
         * AN UNDO MAY ONLY EVER TOUCH THE SHARE IT OWNS.
         */
        if (ownsShare(stopsBefore, stream)) await stopShare();
        return { status: 'stopped', stoppedBy };
      }
      deps.onPublicationsChanged();
      if (!deps.isConnected()) deps.onShareOwnerChanged('local');
      /* PUBLISHED, not merely captured. A source can hand over an audio track
         that the publish then refuses, and telling a presenter their demo has
         sound when the SFU never received it is the same defect as the silence
         this whole path exists to end. */
      return { status: 'started', withAudio: publisher?.screenAudioPublished ?? false };
    },

    stopScreenShare: stopShare,

    get shareStarting() {
      return starting;
    },

    get shareCapturing() {
      return captureLive();
    },

    get shareOrphaned() {
      return published && busy === 0 && !captureLive();
    },

    async restoreOutputDevice() {
      if (outputDeviceId.length > 0) await setOutput(outputDeviceId);
    },
  };
}
